import { useEffect } from 'react';
import { useResizeObserver } from '@direct-frontend/stdlib';
import { getWindow } from '../../../../lib/getWindow';
import { type DrawerProps, type DrawerRefs } from '../types';

type UseDrawerResizeProps = {
    callback: VoidFunction;
} & Pick<DrawerProps, 'isOpen'> &
    Pick<DrawerRefs, 'headerRef' | 'containerRef' | 'contentRef'>;

export const useDrawerResize = ({ containerRef, headerRef, contentRef, callback, isOpen }: UseDrawerResizeProps) => {
    const observer = useResizeObserver(callback);

    useEffect(() => {
        if (observer && isOpen) {
            if (headerRef.current) {
                observer.observe(headerRef.current);
            }

            if (contentRef.current) {
                observer.observe(contentRef.current);

                const children = contentRef.current.children;

                for (const child of Array.from(children)) {
                    observer.observe(child);
                }
            }
        }

        return () => {
            observer?.disconnect();
        };
    }, [containerRef, contentRef, headerRef, isOpen, observer]);

    useEffect(() => {
        const window = getWindow();

        if (!window) {
            return;
        }

        if (!isOpen) {
            return;
        }

        window.addEventListener('resize', callback);

        return () => {
            window.removeEventListener('resize', callback);
        };
    }, [observer, callback, isOpen]);

    return;
};
